import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag, Loader2, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABEL = {
  nudity_sexual: 'Nudity / Sexual',
  violence_graphic: 'Violence / Graphic',
  hate_harassment: 'Hate / Harassment',
  dangerous_self_harm: 'Dangerous / Self-harm',
  illegal_activities: 'Illegal activities',
  misinformation: 'Misinformation',
  spam_scam: 'Spam / Scam',
  copyright: 'Copyright',
  minor_safety: 'Minor safety',
  other: 'Other',
};

export default function AdminReportsTab({ getAuthHeader }) {
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/reports?status=${statusFilter}`, {
        headers: getAuthHeader(),
      });
      setReports(res.data?.reports || []);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [statusFilter]);

  const resolve = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(
        `${API_URL}/api/admin/reports/${id}/resolve`,
        { action },
        { headers: getAuthHeader() }
      );
      toast.success(action === 'uphold' ? 'Report upheld — content hidden.' : 'Report dismissed.');
      fetchReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resolve report');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-reports-tab">
      <div className="flex items-center gap-3">
        <Flag className="w-5 h-5 text-brand-gold" />
        <h2 className="text-xl font-playfair text-white">Content Reports</h2>
        <div className="ml-auto flex gap-1 rounded-lg bg-white/5 p-1">
          {[
            { id: 'open', label: 'Open' },
            { id: 'upheld', label: 'Upheld' },
            { id: 'dismissed', label: 'Dismissed' },
            { id: 'all', label: 'All' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                statusFilter === f.id ? 'bg-brand-gold text-black' : 'text-gray-300 hover:text-white'
              }`}
              data-testid={`report-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-brand-surface border border-white/5 rounded-2xl p-10 text-center">
          <Flag className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No {statusFilter === 'all' ? '' : statusFilter} reports.</p>
        </div>
      ) : (
        <div className="bg-brand-surface border border-white/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-gray-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Reported item</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Reporter</th>
                <th className="text-left px-4 py-3">Details</th>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`report-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-white/5 text-brand-gold text-[10px] uppercase tracking-wider">{r.video_type}</span>
                      <span className="font-mono text-xs text-gray-300 truncate max-w-[180px]">{r.video_id}</span>
                      {r.context_url && (
                        <a href={r.context_url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-brand-gold">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    {r.ai_flagged && (
                      <div className="mt-1 text-[10px] text-amber-400">AI pre-flagged: {r.ai_reason}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-200">{CATEGORY_LABEL[r.category] || r.category}</td>
                  <td className="px-4 py-3 text-gray-300">
                    <div>{r.reporter?.name || '—'}</div>
                    <div className="text-[11px] text-gray-500">{r.reporter?.email || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-300 max-w-xs">
                    <div className="line-clamp-2 text-xs">{r.description || <span className="text-gray-500">—</span>}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.status === 'open' ? (
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => resolve(r.id, 'dismiss')}
                          disabled={busyId === r.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-gray-300 text-xs disabled:opacity-40"
                          data-testid={`dismiss-${r.id}`}
                        >
                          <XCircle className="w-3 h-3" /> Dismiss
                        </button>
                        <button
                          onClick={() => resolve(r.id, 'uphold')}
                          disabled={busyId === r.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-500 hover:bg-red-600 text-white text-xs font-semibold disabled:opacity-40"
                          data-testid={`uphold-${r.id}`}
                        >
                          <CheckCircle2 className="w-3 h-3" /> Uphold
                        </button>
                      </div>
                    ) : (
                      <span className={`text-[11px] px-2 py-0.5 rounded ${
                        r.status === 'upheld' ? 'bg-red-500/15 text-red-300' : 'bg-white/5 text-gray-400'
                      }`}>
                        {r.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
